/**
 * staffcall1 — pure decision helpers for the staff-to-staff direct-call lifecycle.
 *
 * Extracted for the same reason as callFailureGuard/callParticipant: the interesting rules are
 * decisions, and decisions should be testable without mounting a hook or a socket.
 *
 * Background: the legacy direct call used the SDP offer itself as the ring, and offers are
 * delivered single-canonical (one socket per logical peer). With one staff member signed in on
 * several devices, exactly one session could ring — chosen by newest reg_key, so a reconnect moved
 * it. staffcall1 rings every session with a non-SDP invite, elects one winner atomically, and sends
 * SDP only to that winner.
 */

export const STAFFCALL_PROTO = 'staffcall1';

/**
 * A winner election result is only for US if it names the call we are currently ringing.
 * Guards against a late/duplicated/stale event for a previous call resolving the wrong attempt.
 */
export function isWinnerEventForPending(pending, message) {
  if (!pending || !message) return false;
  if (!message.call_id || !pending.callId) return false;
  return message.call_id === pending.callId;
}

/**
 * Where the SDP offer must go. The winner is an EXACT conn_id ("<baseUid>__<sessionId>"); using the
 * base uid here would re-enter newest-reg_key selection and reintroduce the defect.
 */
export function sdpTargetFor(outcome, legacyTarget) {
  if (outcome && outcome.mode === 'winner' && outcome.winner) return outcome.winner;
  return legacyTarget;
}

/** True when the ring ended with nobody accepting (a human deadline, not a protocol decision). */
export function isNoAnswer(outcome) {
  return !!outcome && outcome.mode === 'noanswer';
}

/** True when the backend told us a legacy sibling session exists -> whole call uses the legacy path. */
export function isLegacyFallback(outcome) {
  return !!outcome && outcome.mode === 'legacy';
}

/**
 * Caller display name for the ringing UI. The invite's server-resolved name is AUTHORITATIVE — it
 * replaces the old lookup against a cached online-roster, which rendered the literal "Staff member"
 * whenever the caller was missing from a stale snapshot. Only fall back when the server could not
 * resolve a name at all.
 */
export function callerDisplayName(invite, fallback) {
  const fromServer = invite && typeof invite.caller_name === 'string' ? invite.caller_name.trim() : '';
  if (fromServer) return fromServer;
  return (fallback && String(fallback).trim()) || '';
}

/** A session already on a call must not ring for a new invite. */
export function canRingInvite({ currentCall, hasPeerConnection }) {
  return !currentCall && !hasPeerConnection;
}

/**
 * A losing session must clear its pending invite — and ONLY for the call it is actually ringing on.
 * A cancel naming a different call must never dismiss the current one.
 */
export function shouldDismissInvite(pendingInvite, message) {
  if (!pendingInvite || !message) return false;
  if (!message.call_id) return false;
  return pendingInvite.call_id === message.call_id;
}

/** The armed winner answers its own offer in place instead of ringing again. */
export function isArmedOfferForUs(armedCallId, message) {
  return !!armedCallId && !!message && message.call_id === armedCallId;
}

/**
 * Discriminate a REAL explicit hand-off from a DOM/Synthetic click event.
 *
 * The ringing UI binds `onClick={onAccept}`, so acceptCall's first argument is frequently a click
 * event rather than a hand-off. Treating any truthy first argument as "explicit" made the invite
 * path fall through to the SDP path, where a staffcall1 invite has no held offer yet — so accept
 * returned silently and the call could not be answered on ANY session.
 *
 * Only an object actually carrying the winner path's payload counts.
 */
export function explicitHandoff(arg) {
  if (!arg || typeof arg !== 'object') return null;
  if (typeof arg.preventDefault === 'function' || arg.nativeEvent) return null;   // click event
  return (arg.message || arg.incoming) ? arg : null;
}
